import { Eye, EyeOff } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'

import { Button } from '../../ui/Button/Button'
import { Input } from '../../ui/Input/Input'
import { Spinner } from '../../ui/Spinner/Spinner'
import { useAuth } from './AuthProvider'
import styles from './LoginPage.module.css'

export type LoginRouteState = {
  email?: string
  from?: string
  message?: string
  reauthenticate?: boolean
}

const MANAGER_INVITATION_RETURN_PATH =
  /^\/manager-invitations\/claim\?token=[A-Za-z0-9_-]{43}$/

function managerInvitationReturnPath(value: unknown) {
  return typeof value === 'string' && MANAGER_INVITATION_RETURN_PATH.test(value)
    ? value
    : null
}

function firebaseAuthErrorCode(error: unknown) {
  if (!error || typeof error !== 'object' || !('code' in error))
    return 'unknown'
  const code = typeof error.code === 'string' ? error.code : ''
  return /^auth\/[a-z0-9-]+$/.test(code) ? code : 'unknown'
}

function signInErrorMessage(code: string) {
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return 'The email or password is incorrect. Check your credentials and try again.'
    case 'auth/user-disabled':
      return 'This account is disabled. Contact Flio support for help.'
    case 'auth/network-request-failed':
      return 'Flio could not reach the sign-in service. Check your connection and try again.'
    case 'auth/too-many-requests':
      return 'Too many sign-in attempts. Wait a moment, then try again.'
    case 'auth/operation-not-allowed':
      return 'Password sign-in is not available for this account. Contact Flio support for help.'
    default:
      return 'Unable to sign in right now. Try again or contact Flio support.'
  }
}

function resetErrorMessage(code: string) {
  switch (code) {
    case 'auth/invalid-email':
    case 'auth/missing-email':
      return 'Enter a valid email address.'
    case 'auth/network-request-failed':
      return 'Flio could not reach the password-reset service. Check your connection and try again.'
    case 'auth/too-many-requests':
      return 'Too many password-reset attempts. Wait a moment, then try again.'
    case 'auth/operation-not-allowed':
      return 'Password reset is temporarily unavailable. Contact Flio support for help.'
    default:
      return 'Flio could not request a password-reset email. Try again or contact Flio support.'
  }
}

// After setting a password, Firebase returns to sign-in, which continues the
// invitation it came from.
export function resetContinueUrl(invitationReturnPath: string | null) {
  if (typeof window === 'undefined') return undefined
  const result = new URL('/login', window.location.origin)
  if (invitationReturnPath) {
    result.searchParams.set('returnTo', invitationReturnPath)
  }
  return result.toString()
}

export function LoginPage() {
  const auth = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const routeState = location.state as LoginRouteState | null
  const queryReturnPath = managerInvitationReturnPath(
    new URLSearchParams(location.search).get('returnTo')
  )
  const invitationReturnPath =
    managerInvitationReturnPath(routeState?.from) ?? queryReturnPath
  const returnPath = invitationReturnPath ?? routeState?.from ?? '/'
  const [email, setEmail] = useState(routeState?.email ?? '')
  const [password, setPassword] = useState('')
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [resetMode, setResetMode] = useState(false)
  const [resetRequested, setResetRequested] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (auth.status === 'hydrating') {
    return <Spinner fullPage label="Restoring your session" />
  }

  if (
    auth.status === 'authenticated' &&
    !routeState?.reauthenticate &&
    !queryReturnPath
  ) {
    return <Navigate to="/" replace />
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      await auth.login(email.trim(), password)
      void navigate(returnPath, { replace: true })
    } catch (failure) {
      const authErrorCode = firebaseAuthErrorCode(failure)
      // Retain only the bounded provider code. Never log the email, password,
      // invitation return path/token, or the provider's raw error message.
      console.warn('dashboard_auth_sign_in_failed', { authErrorCode })
      setError(signInErrorMessage(authErrorCode))
    } finally {
      setSubmitting(false)
    }
  }

  async function handlePasswordReset(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      await auth.sendPasswordReset(
        email.trim(),
        resetContinueUrl(invitationReturnPath)
      )
      setResetRequested(true)
    } catch (failure) {
      const authErrorCode = firebaseAuthErrorCode(failure)
      // Firebase may return this when email-enumeration protection is disabled.
      // Treat it exactly like an accepted request and do not retain it in client
      // diagnostics, so the reset UI never becomes an account lookup surface.
      if (authErrorCode === 'auth/user-not-found') {
        setResetRequested(true)
      } else {
        console.warn('dashboard_auth_password_reset_failed', { authErrorCode })
        setError(resetErrorMessage(authErrorCode))
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (resetMode) {
    if (resetRequested) {
      return (
        <section className={styles.form}>
          <h2>Check your email</h2>
          <p>
            If an account is associated with that email, Firebase will send a
            password-reset link. After setting a new password, return to Flio
            and sign in.
          </p>
          <Button
            className={styles.submit}
            type="button"
            onClick={() => {
              setResetMode(false)
              setResetRequested(false)
              setError(null)
            }}
          >
            Back to sign in
          </Button>
        </section>
      )
    }
    return (
      <form
        className={styles.form}
        onSubmit={(event) => void handlePasswordReset(event)}
      >
        <h2>Reset your password</h2>
        <p>
          Enter your account email. We’ll send password-reset instructions if an
          account is associated with it.
        </p>
        <div className={styles.fields}>
          <Input
            id="reset-email"
            label="Email"
            type="email"
            value={email}
            autoComplete="email"
            required
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        {error ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}
        <Button className={styles.submit} type="submit" disabled={submitting}>
          {submitting ? 'Sending…' : 'Send reset email'}
        </Button>
        <Button
          className={styles.secondaryButton}
          type="button"
          variant="ghost"
          disabled={submitting}
          onClick={() => {
            setResetMode(false)
            setError(null)
          }}
        >
          Back to sign in
        </Button>
      </form>
    )
  }

  return (
    <form
      className={styles.form}
      onSubmit={(event) => void handleSubmit(event)}
    >
      <h2>Welcome back</h2>
      <p>Sign in with your clinic account.</p>
      <div className={styles.fields}>
        <Input
          id="email"
          label="Email"
          type="email"
          value={email}
          autoComplete="email"
          required
          onChange={(event) => setEmail(event.target.value)}
        />
        <div className={styles.passwordField}>
          <Input
            id="password"
            label="Password"
            type={passwordVisible ? 'text' : 'password'}
            value={password}
            autoComplete="current-password"
            required
            onChange={(event) => setPassword(event.target.value)}
          />
          <button
            className={styles.passwordToggle}
            type="button"
            aria-label={passwordVisible ? 'Hide password' : 'Show password'}
            aria-pressed={passwordVisible}
            onClick={() => setPasswordVisible((visible) => !visible)}
          >
            {passwordVisible ? (
              <EyeOff size={18} aria-hidden="true" />
            ) : (
              <Eye size={18} aria-hidden="true" />
            )}
          </button>
        </div>
        <button
          className={styles.forgotPassword}
          type="button"
          onClick={() => {
            setResetMode(true)
            setError(null)
          }}
        >
          Forgot password?
        </button>
      </div>
      {routeState?.message ? <p role="status">{routeState.message}</p> : null}
      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
      <Button className={styles.submit} type="submit" disabled={submitting}>
        {submitting ? 'Signing in…' : 'Sign in'}
      </Button>
      <p className={styles.secondaryAction}>
        New clinic owner? <Link to="/signup">Create an account</Link>
      </p>
    </form>
  )
}
